import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AiAgentApi } from './AiAgentApi';
import type { ChatRequest, AgentResponse, AgentInfo } from './types';

/**
 * Client for the Golden Path AI Agent
 */
export class AiAgentClient implements AiAgentApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    // Use proxy endpoint configured in backend
    return await this.discoveryApi.getBaseUrl('proxy');
  }

  async sendMessage(request: ChatRequest): Promise<AgentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ai-agent/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    const text = await response.text();
    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      if (response.status === 504 || response.status === 502) {
        throw new Error(
          'The agent is still processing — code generation can take up to 2 minutes with LLM rate limiting. Please wait a moment and try again.',
        );
      }
      throw new Error(
        `Unexpected response from agent (HTTP ${response.status}). Please try again.`,
      );
    }

    if (!response.ok) {
      throw new Error(data.detail || data.error || data.response || 'Failed to send message to agent');
    }

    return data as AgentResponse;
  }

  async getAgentInfo(): Promise<AgentInfo[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ai-agent/agents`,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch agent information');
    }

    return await response.json();
  }

  async healthCheck(): Promise<{ status: string; agent: string; version: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ai-agent/health`,
    );

    if (!response.ok) {
      throw new Error('Agent health check failed');
    }

    return await response.json();
  }
}
